import { BillingInfo } from '~/types/ResponseType'
import { fetchWrapper } from '~/utils/fetchWrapper'
import { extractApiErrorMessage } from '~/utils/shared'

export interface ValidationErrorDetail {
  type: string
  loc: string[]
  msg: string
  input: string
  ctx?: {
    error: Record<string, unknown>
  }
}

export interface APIError {
  detail: ValidationErrorDetail[] | string
}

export interface PaymentSetupResponse {
  redirect_url: string
}

export interface PaymentDetailsResponse {
  has_payment_method: boolean
  payment_method_last4?: string
  payment_method_brand?: string
  payment_method_exp_month?: number
  payment_method_exp_year?: number
}

export interface CustomerPortalResponse {
  url: string
  redirect_url?: string
}

// Pricing configuration - synced via Stripe webhooks
export interface LicensePricingConfig {
  price: number
  per_user_price?: number | null
  per_user_six_month_price?: number | null
  subscription_six_month_price?: number | null
  six_month_price?: number | null
  six_month_discount_percent?: number | null
  packages?: Record<
    string,
    {
      price?: number | null
      per_user_price?: number | null
      package_months?: number | null
      discount_percent?: number | null
      stripe_price_id?: string | null
    }
  > | null
  currency: string
  interval: string // 'monthly' | 'six_month' | 'per_user_monthly' | 'custom'
  billing_model: string // 'prepaid' | 'subscription' | 'per_seat' | 'custom'
  stripe_price_id?: string | null
  six_month_stripe_price_id?: string | null
}

// License-based plan response from /api/license/type
export interface LicenseTypeMetadata {
  type: string // 'usage_based' | 'subscription' | 'flat_rate' | 'hybrid'
  duration: number
  duration_unit: string // 'day' | 'week' | 'month' | 'year'
  quota: number | null
  usage_quotas: Record<string, { limit_value: number; reset_interval: string }> | null
  pricing?: LicensePricingConfig | null // Pricing info - synced via Stripe webhooks
  features: string[] | null // Features list for display in UI
  credit_limit?: number
  user_limit?: number
  per_user_price?: number | null
  subscription_price?: number | null
}

export interface LicenseTypeResponse {
  id: number
  name: string // 'flex' | 'pro' | 'pro_plus' | 'team' | 'enterprise' | etc.
  label: string // Display name: 'Flex', 'Pro', 'Pro+', 'Teams', 'Enterprise'
  description: string
  price: number // Price from DB (synced via Stripe webhooks)
  currency: string // Currency code (e.g., 'USD')
  interval: string // Billing interval ('monthly', 'per_user_monthly', 'custom')
  metadata: LicenseTypeMetadata
}

export interface SubscriptionResponse {
  subscription_id?: string
  status: string
  current_period_start?: string
  current_period_end?: string
  cancel_at_period_end: boolean
  plan_id?: string
  plan_name?: string
  amount?: number
  currency?: string
}

export interface BillingAccountsForUserResponse {
  id: number
  name: string
  is_billing_admin: boolean
  billing_email: string
}

export interface CreateBillingAccountRequest {
  billing_email: string
  billing_to: string
  account_type?: string
  org_id: number
}

export interface BillingAccountResponse {
  id: number
  billing_email: string
  billing_to: string | null
  created_time: string
  updated_time: string
  billing_metadata: Record<string, unknown>
}

export interface BillingSummaryResponse {
  current_period_start?: string
  current_period_end?: string
  amount_due?: number
  amount_paid?: number
  currency?: string
  status: string
  next_payment_date?: string
  usage_this_period?: {
    task_limit?: number
    monthly_usage?: Record<string, number>
  }
}

export interface InvoiceResponse {
  invoice_id: string
  amount: number
  currency: string
  status: string
  created: string
  due_date?: string
  paid: boolean
  pdf_url?: string
}

export const getPaymentDetails = async (
  billingAccountId: number
): Promise<PaymentDetailsResponse> => {
  return fetchWrapper.get(`/api/billing/${billingAccountId}/payment-details`)
}

export const createCustomerPortalSession = async (
  billingAccountId: number
): Promise<CustomerPortalResponse> => {
  return fetchWrapper.get(`/api/billing/${billingAccountId}/customer-portal`)
}

export const cancelSubscription = async (
  billingAccountId: number,
  licenseInstanceId: number
): Promise<{ success: boolean; message?: string }> => {
  return fetchWrapper.post(`/api/billing/${billingAccountId}/licenses/${licenseInstanceId}/cancel`)
}

export const revertCancellation = async (
  billingAccountId: number,
  licenseInstanceId: number
): Promise<{ success: boolean; message?: string }> => {
  return fetchWrapper.post(
    `/api/billing/${billingAccountId}/licenses/${licenseInstanceId}/revert-cancel`
  )
}

export const getBillingInfoById = async (billingAccountId: number): Promise<BillingInfo> => {
  // Fetch the billing info using the specific billing account ID
  const response = await fetchWrapper(`/api/billing/${billingAccountId}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    }
  })
  if (!response.ok) {
    const error = await response.text()
    throw new Error(error)
  }
  const data: BillingInfo = await response.json()
  return data
}

export const updateBillingInfo = async (
  billing_id: number,
  billing_to?: string,
  billing_email?: string,
  name?: string
): Promise<BillingAccountResponse> => {
  const reqParam: Record<string, string> = {}

  if (billing_to) reqParam.billing_to = billing_to
  if (billing_email) reqParam.billing_email = billing_email
  if (name) reqParam.name = name

  const response = await fetchWrapper(`/api/billing/${billing_id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(reqParam)
  })

  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to update billing info. Reason: ${reason}`)
  }

  return response.json()
}

export const getCustomerPortalUrl = async (
  billingAccountId: number
): Promise<PaymentSetupResponse> => {
  return fetchWrapper.get(`/api/billing/${billingAccountId}/portal`)
}

/**
 * Get available license types for upgrade based on current billing account.
 * Backend filters to only return plans the user can upgrade to.
 */
export const getUpgradeableLicenseTypes = async (
  billingId: number
): Promise<LicenseTypeResponse[]> => {
  const response = await fetchWrapper(`/api/billing/${billingId}/license_types`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    }
  })
  if (!response.ok) {
    const error = await response.text()
    throw new Error(error)
  }
  return await response.json()
}

export const getCurrentSubscription = async (
  billingAccountId: number
): Promise<SubscriptionResponse> => {
  const response = await fetchWrapper(`/api/billing/${billingAccountId}/subscription`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    }
  })
  if (!response.ok) {
    const error = await response.text()
    throw new Error(error)
  }
  return response.json()
}

export const getBillingSummary = async (
  billingAccountId: number
): Promise<BillingSummaryResponse> => {
  const response = await fetchWrapper(`/api/billing/${billingAccountId}/billing`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    }
  })
  if (!response.ok) {
    const error = await response.text()
    throw new Error(error)
  }
  return response.json()
}

export const getInvoices = async (
  billingAccountId: number,
  limit: number = 10
): Promise<InvoiceResponse[]> => {
  const response = await fetchWrapper(`/api/billing/${billingAccountId}/invoices?limit=${limit}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    }
  })
  if (!response.ok) {
    const error = await response.text()
    throw new Error(error)
  }
  return response.json()
}

export const getUserBillingAccounts = async (): Promise<BillingAccountsForUserResponse[]> => {
  const response = await fetchWrapper('/api/billing/', {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    }
  })
  if (!response.ok) {
    const error = await response.text()
    throw new Error(error)
  }
  return response.json()
}

export const createBillingAccount = async (
  request: CreateBillingAccountRequest
): Promise<BillingAccountResponse> => {
  const response = await fetchWrapper('/api/billing/', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(request)
  })
  if (!response.ok) {
    const error = await response.text()
    throw new Error(error)
  }
  return response.json()
}

export interface LicenseQuota {
  credits_limit: number // -1 for unlimited
  user_limit: number // -1 for unlimited
  unlimited_credits: boolean
  unlimited_users: boolean
  billable_flows_limit?: number | null // Billable flows quota limit
}

export interface LicenseSummary {
  instance_id: number
  license_name: string
  license_label: string
  quota: LicenseQuota
  start_date?: string
  end_date?: string
  subscription_id?: string
}

// NOTE: UpdateAllowOverageRequest and AllowOverageResponse removed
// Overage is automatic for paid plans - no user toggle needed

export interface TeamMember {
  user_id: number
  name: string
  email: string
  is_billing_admin: boolean
  workspaces: Array<{
    org_id: number
    org_name: string
    role: string
    added_time: string
  }>
}

export const getTeamMembers = async (
  billingAccountId: number,
  orgId: number
): Promise<TeamMember[]> => {
  const response = await fetchWrapper(`/api/billing/${billingAccountId}/users/org/${orgId}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    }
  })
  if (!response.ok) {
    const error = await response.text()
    throw new Error(error)
  }
  return response.json()
}

// Multi-license support APIs

export interface LicenseResponse {
  instance_id: number
  license_name: string
  license_label: string
  status: string
  start_date?: string
  end_date?: string
  quota: LicenseQuota
  subscription_id?: string
  is_free_license: boolean
  features: string[]
  is_billing_period: boolean // Whether this license's dates define the billing period
  auto_renew: boolean // Whether the license auto-renews (synced with Stripe)
  billing_package?: string | null // Billing cadence, e.g. monthly or six_month
  credit_period_start?: string | null
  credit_period_end?: string | null
}

export interface AllLicensesResponse {
  // Account info
  billing_account_id: number
  has_enterprise_access: boolean
  can_manage_licenses: boolean
  license_management_message: string
  // Licenses
  total_licenses: number
  licenses: LicenseResponse[]
  // Usage & Quota (included directly from backend)
  credits_used: number
  credits_limit: number
  credits_remaining: number
  utilization_percent: number
  user_count: number
  user_limit: number
  billable_flows_limit?: number | null // Total billable flows limit across all licenses
  billable_flows_used?: number // Billable flows (tasks) used in billing period
  // Billing period (present for active subscriptions)
  period_start?: string
  period_end?: string
  credit_period_start?: string | null
  credit_period_end?: string | null
  access_period_start?: string | null
  access_period_end?: string | null
  billing_period_license_name?: string // Name of the license defining the billing period
  // Plan flags
  is_free_license: boolean
  // Billable flow plan flags
  is_billable_flow_plan?: boolean // Whether this is a billable flow plan (pays per migration task)
  hide_credits?: boolean // Whether to hide credit usage from users (true for billable flow plans)
}

export const getAllLicenses = async (billingAccountId: number): Promise<AllLicensesResponse> => {
  return fetchWrapper.get<AllLicensesResponse>(`/api/billing/${billingAccountId}/licenses`)
}

// Account membership management APIs

export interface AccountMember {
  id: number
  billing_id: number
  user_id: number
  role: string
  username: string
  name: string
  email: string
}

export const getAccountMembers = async (billingAccountId: number): Promise<AccountMember[]> => {
  const response = await fetchWrapper(`/api/billing/${billingAccountId}/member`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    }
  })
  if (!response.ok) {
    const error = await response.text()
    throw new Error(error)
  }
  return response.json()
}

export const addAccountMember = async (
  billingAccountId: number,
  userId: number,
  role: string = 'admin'
): Promise<{ message: string; membership: AccountMember }> => {
  const response = await fetchWrapper(`/api/billing/${billingAccountId}/member`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ user_id: userId, role })
  })
  if (!response.ok) {
    const error = await response.json()
    throw new Error(extractApiErrorMessage(error, 'Failed to add account member'))
  }
  return response.json()
}

export const removeAccountMember = async (
  billingAccountId: number,
  userId: number
): Promise<{ message: string }> => {
  const response = await fetchWrapper(`/api/billing/${billingAccountId}/member/${userId}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json'
    }
  })
  if (!response.ok) {
    const error = await response.json()
    throw new Error(extractApiErrorMessage(error, 'Failed to remove account member'))
  }
  return response.json()
}

export const cancelAccountInvitation = async (
  billingAccountId: number,
  invitationId: number
): Promise<{ message: string; invitation_id: number }> => {
  const response = await fetchWrapper(
    `/api/billing/${billingAccountId}/invitations/${invitationId}`,
    {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json'
      }
    }
  )
  if (!response.ok) {
    const error = await response.json()
    throw new Error(extractApiErrorMessage(error, 'Failed to cancel invitation'))
  }
  return response.json()
}

// ============================================================================
// Invite Members by Email
// ============================================================================

/**
 * Type of invitation to send
 * - account_admin: Full admin access to the billing account
 * - all_workspaces: Member access to all current workspaces
 * - specific_workspaces: Member access to selected workspaces only
 */
export type InviteType = 'account_admin' | 'all_workspaces' | 'specific_workspaces'

export interface InviteMemberByEmailRequest {
  emails: string[]
  invite_type: InviteType
  workspace_ids?: number[] // Required when invite_type is 'specific_workspaces'
}

export interface InviteMemberByEmailResponse {
  success: boolean
  message: string
  invited: string[]
  failed: string[]
}

export const inviteAccountMembersByEmail = async (
  billingAccountId: number,
  request: InviteMemberByEmailRequest
): Promise<InviteMemberByEmailResponse> => {
  const response = await fetchWrapper(`/api/billing/${billingAccountId}/invite-members`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(request)
  })
  if (!response.ok) {
    const error = await response.json()
    throw new Error(extractApiErrorMessage(error, 'Failed to send invitations'))
  }
  return response.json()
}

// ============================================================================
// Billing Account Invitation Link
// ============================================================================

export interface InvitationLinkResponse {
  invitation_code: string | null
}

export interface JoinByInvitationResponse {
  success: boolean
  message: string
  billing_id: number
  account_name: string
  already_member: boolean
}

export const getBillingInvitationLink = async (
  billingAccountId: number
): Promise<InvitationLinkResponse> => {
  const response = await fetchWrapper(`/api/billing/${billingAccountId}/invite`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    }
  })
  if (!response.ok) {
    const error = await response.text()
    throw new Error(error)
  }
  return response.json()
}

export const setBillingInvitationLink = async (
  billingAccountId: number,
  enabled: boolean
): Promise<InvitationLinkResponse> => {
  const response = await fetchWrapper(`/api/billing/${billingAccountId}/invite`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ enabled })
  })
  if (!response.ok) {
    const error = await response.text()
    throw new Error(error)
  }
  return response.json()
}

export const joinBillingAccount = async (
  invitationCode: string
): Promise<JoinByInvitationResponse> => {
  const response = await fetchWrapper('/api/billing/join', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ invitation_code: invitationCode })
  })
  if (!response.ok) {
    const error = await response.json()
    throw new Error(extractApiErrorMessage(error, 'Failed to join billing account'))
  }
  return response.json()
}

// NOTE: updateLicenseAllowOverage function removed
// Overage is automatic for paid plans - no user toggle needed

// ============================================================================
// Credit Purchase APIs (Prepay System)
// ============================================================================

export interface CreditPackInfo {
  id: string
  credits: number
  price_cents: number
  price_display: string
}

export interface CreditPacksResponse {
  packs: CreditPackInfo[]
  price_per_credit_cents: number
  min_custom_amount: number
  max_custom_amount: number
}

export interface CreditBalanceResponse {
  total_credits: number
  subscription_remaining: number
  purchased_credits: number
  subscription_limit: number
  subscription_used: number
  period_start?: string
  period_end?: string
  purchase_history: Array<{
    credits: number
    purchased_at?: string
    pack_type: string
    refunded?: boolean
    amount_total_cents?: number
    currency?: string
    receipt_url?: string
  }>
}

export interface CreditPurchaseRequest {
  pack_type?: string
  custom_amount?: number
  success_url: string
  cancel_url: string
}

export interface CreditPurchaseResponse {
  redirect_url: string
  checkout_session_id: string
}

export interface CreditPurchaseHistoryResponse {
  purchases: Array<{
    id: number
    amount: number
    remaining: number
    used: number
    purchased_at?: string
    expires_at?: string
    status: string
    stripe_payment_intent_id?: string
  }>
}

/**
 * Account lock status response - indicates if account is locked due to credit debt
 */
export interface AccountLockStatusResponse {
  account_locked: boolean
  lock_reason: string | null
  locked_at: string | null
  credit_debt: number
  minimum_credits_to_unlock: number
  credits_used: number
  credits_limit: number
  effective_limit: number
}

/**
 * Get available credit packs for purchase
 */
export const getCreditPacks = async (): Promise<CreditPacksResponse> => {
  return fetchWrapper.get<CreditPacksResponse>('/api/billing/credits/packs')
}

/**
 * Get current credit balance breakdown
 * @param billingId - Billing account ID
 */
export const getCreditBalance = async (billingId: number): Promise<CreditBalanceResponse> => {
  return fetchWrapper.get<CreditBalanceResponse>(`/api/billing/${billingId}/credits/balance`)
}

/**
 * Create a checkout session for purchasing credits
 * @param billingId - Billing account ID
 * @param packType - Predefined pack type (small, medium, large, bulk) or null for custom
 * @param customAmount - Custom number of credits (1-500) if packType is null
 */
export const purchaseCredits = async (
  billingId: number,
  packType: string | null,
  successUrl: string,
  cancelUrl: string,
  customAmount?: number
): Promise<CreditPurchaseResponse> => {
  const body: CreditPurchaseRequest = {
    success_url: successUrl,
    cancel_url: cancelUrl
  }
  if (packType) {
    body.pack_type = packType
  } else if (customAmount) {
    body.custom_amount = customAmount
  }

  const response = await fetchWrapper(`/api/billing/${billingId}/credits/purchase`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(body)
  })
  if (!response.ok) {
    const error = await response.json()
    throw new Error(extractApiErrorMessage(error, 'Failed to create credit purchase'))
  }
  return response.json()
}

/**
 * Flex activation request - activates the Flex (pay-as-you-go) plan
 */
export interface FlexActivationRequest {
  pack_type?: string
  custom_amount?: number
  success_url: string
  cancel_url: string
}

/**
 * Flex activation response - contains checkout URL for initial credit purchase
 */
export interface FlexActivationResponse {
  redirect_url: string
  checkout_session_id: string
  credits: number
  price_cents: number
}

/**
 * Activate the Flex plan by purchasing initial credits.
 * Only available for Flex-tier users.
 * @param billingId - Billing account ID
 * @param packType - Credit pack type (e.g., 'starter')
 * @param successUrl - URL to redirect after successful payment
 * @param cancelUrl - URL to redirect if payment is cancelled
 * @param customAmount - Custom number of credits (optional)
 */
export const activateFlex = async (
  billingId: number,
  packType: string | null,
  successUrl: string,
  cancelUrl: string,
  customAmount?: number
): Promise<FlexActivationResponse> => {
  const body: FlexActivationRequest = {
    success_url: successUrl,
    cancel_url: cancelUrl
  }
  if (packType) {
    body.pack_type = packType
  } else if (customAmount) {
    body.custom_amount = customAmount
  }

  const response = await fetchWrapper(`/api/billing/${billingId}/credits/activate-flex`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(body)
  })
  if (!response.ok) {
    const error = await response.json()
    throw new Error(extractApiErrorMessage(error, 'Failed to activate Flex plan'))
  }
  return response.json()
}

/**
 * Get credit purchase history
 * @param billingId - Billing account ID
 */
export const getCreditPurchaseHistory = async (
  billingId: number,
  includeExpired: boolean = false
): Promise<CreditPurchaseHistoryResponse> => {
  return fetchWrapper.get<CreditPurchaseHistoryResponse>(
    `/api/billing/${billingId}/credits/purchases?include_expired=${includeExpired}`
  )
}

/**
 * Get account lock status for credit debt
 * @param billingId - Billing account ID
 * @returns Account lock status including debt amount and unlock requirements
 */
export const getAccountLockStatus = async (
  billingId: number
): Promise<AccountLockStatusResponse> => {
  return fetchWrapper.get<AccountLockStatusResponse>(`/api/billing/${billingId}/lock-status`)
}

/**
 * Debt payment request - used to pay off account debt and unlock
 */
export interface DebtPaymentRequest {
  success_url: string
  cancel_url: string
  extra_credits?: number
}

/**
 * Debt payment response - contains checkout URL for paying debt
 */
export interface DebtPaymentResponse {
  checkout_url: string
  debt_amount: number
  extra_credits: number
  total_credits: number
  total_price_cents: number
}

/**
 * Create a checkout session to pay off account debt and unlock
 * @param billingId - Billing account ID
 * @param successUrl - URL to redirect after successful payment
 * @param cancelUrl - URL to redirect if payment is cancelled
 * @param extraCredits - Optional extra credits to purchase beyond debt
 * @returns Checkout URL and payment details
 */
export const createDebtPayment = async (
  billingId: number,
  successUrl: string,
  cancelUrl: string,
  extraCredits: number = 0
): Promise<DebtPaymentResponse> => {
  const body: DebtPaymentRequest = {
    success_url: successUrl,
    cancel_url: cancelUrl,
    extra_credits: extraCredits
  }

  const response = await fetchWrapper(`/api/billing/${billingId}/pay-debt`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(body)
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(extractApiErrorMessage(error, 'Failed to create debt payment'))
  }

  return response.json()
}
